import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, Wallet, Receipt, Menu, X, CheckCircle2, Home as HomeIcon, ShieldCheck } from 'lucide-react';
import MobileMenu from './layout/MobileMenu';
import TopNav from './layout/TopNav';
import UserMenu from './layout/UserMenu';
import Footer from './Footer';
import { NAVBAR_BG, NAVBAR_BORDER } from './layout/constants';
import { isStaffOrAbove } from '../lib/tier';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [adminUnread, setAdminUnread] = useState(0);
  const [userUnread, setUserUnread] = useState(0);

  // Static sub-links surfaced under the Home dropdown.
  const homeSubLinks = [
    { label: 'Pricing', href: '/pricing' },
    { label: 'Help & FAQ', href: '/help' },
    { label: 'Journal', href: '/blog' },
    { label: 'About us', href: '/about' },
    { label: 'Contact us', href: '/contact' },
  ];

  // Admin / staff dropdown — only surfaced to staff+. Keeps every admin
  // page one click away from the navbar (no more guessing URLs).
  const adminSubLinks = isStaffOrAbove(user) ? [
    { label: 'Metrics', href: '/admin/metrics' },
    { label: `Inbox${adminUnread > 0 ? ` (${adminUnread})` : ''}`, href: '/admin/messages' },
    { label: 'Waitlist', href: '/admin/waitlist' },
    { label: 'Email test', href: '/admin/email-test' },
    { label: 'Releases', href: '/admin/releases' },
    { label: 'Feature flags', href: '/admin/feature-flags' },
    { label: 'User tiers', href: '/admin/tiers' },
    { label: 'Moderation', href: '/admin/moderation' },
    { label: 'Blog posts', href: '/admin/blog' },
    { label: 'Edit roadmap', href: '/admin/roadmap' },
    { label: 'Edit home copy', href: '/admin/site-settings' },
    { label: 'VAT monitor', href: '/admin/vat' },
    { label: 'QA checklist', href: '/admin/qa-checklist' },
    { label: 'Pre-deploy checklist', href: '/admin/pre-deploy-checklist' },
  ] : [];

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user) {
        if (!cancelled) setAccounts([]);
        return;
      }
      try {
        const res = await axios.get(`${API}/accounts`, { withCredentials: true });
        if (!cancelled) setAccounts(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setAccounts([]);
      }
    };
    load();
    // Re-fetch when route changes so newly added/removed accounts show in the dropdown.
    return () => { cancelled = true; };
  }, [user, location.pathname]);

  // Poll unread message counts every 60s. Two separate calls:
  //  - admin count drives the "Inbox (N)" badge in the staff dropdown
  //  - user count surfaces a dot on the user menu so customers know
  //    they have a new reply waiting in /account/settings?tab=messages.
  useEffect(() => {
    if (!user) {
      setAdminUnread(0);
      setUserUnread(0);
      return undefined;
    }
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const userR = await axios.get(`${API}/contact/threads/mine/unread-count`, { withCredentials: true });
        if (!cancelled) setUserUnread(userR.data?.unread || 0);
      } catch { /* swallow — not critical */ }
      if (isStaffOrAbove(user)) {
        try {
          const adminR = await axios.get(`${API}/admin/messages/unread-count`, { withCredentials: true });
          if (!cancelled) setAdminUnread(adminR.data?.unread || 0);
        } catch { /* swallow */ }
      }
    };
    fetchCounts();
    const id = setInterval(fetchCounts, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Home', href: '/home', icon: HomeIcon },
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Accounts', href: '/accounts', icon: Wallet },
    { name: 'Transactions', href: '/transactions', icon: Receipt },
    { name: 'Reconcile', href: '/reconcile', icon: CheckCircle2 },
    ...(adminSubLinks.length ? [{ name: 'Admin', href: '/admin/metrics', icon: ShieldCheck }] : []),
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <header
        className="sticky top-0 z-50 border-b shadow-sm print-hide"
        style={{ backgroundColor: NAVBAR_BG, borderBottomColor: NAVBAR_BORDER }}
        data-testid="app-navbar"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2 group">
            <img
              src="https://customer-assets.emergentagent.com/job_budget-forecast-27/artifacts/w1b6b3hu_mini.png"
              alt="Forge Ledger"
              className="w-9 h-9 rounded-lg object-cover ring-1 ring-white/10"
              data-testid="app-logo"
            />
            <span className="text-xl font-bold text-white tracking-tight">Forge Ledger</span>
          </Link>

          <TopNav
            navigation={navigation}
            homeSubLinks={homeSubLinks}
            adminSubLinks={adminSubLinks}
            accounts={accounts}
            currentPath={location.pathname}
          />

          <div className="flex items-center gap-4">
            <UserMenu user={user} onLogout={handleLogout} unreadCount={userUnread} />

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white/80 hover:text-white"
              data-testid="navbar-mobile-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <MobileMenu
            navigation={navigation}
            homeSubLinks={homeSubLinks}
            adminSubLinks={adminSubLinks}
            accounts={accounts}
            user={user}
            currentPath={location.pathname}
            onClose={() => setMobileMenuOpen(false)}
            onLogout={handleLogout}
          />
        )}
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto">
        <Outlet />
      </main>

      <Footer variant="full" />
    </div>
  );
};

export default Layout;
